import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import type { ToastLike } from '@mangovoice/mango-ui'
import type { ExtensionInstance } from '@mangovoice/types'

import type { SwitchboardApi } from '../../../api/switchboardApi'
import { useUpdateSwitchboard } from '../../../queries/hooks'
import type { SwitchboardTranslateFn } from '../../../types'
import { text } from '../../../utils/text'

export interface UseSwitchboardMetadataProps {
  /** Switchboard data */
  switchboard: { extension_dial_plan: number; updated_at: string }
  /** Extension instance data */
  extension: ExtensionInstance | undefined
  /** Whether the edit dialog is open */
  isEditDialogOpen: boolean
  /** Switchboard API instance */
  api: SwitchboardApi
  /** Optional toast function for notifications */
  toast?: ToastLike
  /** Callback on successful update */
  onSuccess?: () => void
  /** Optional translation function for i18n */
  t?: SwitchboardTranslateFn
}

export interface UseSwitchboardMetadataReturn {
  /** Current name value */
  name: string
  /** Set name value */
  setName: (value: string) => void
  /** Current number value */
  number: string
  /** Set number value */
  setNumber: (value: string) => void
  /** Current omit from directory value */
  omitFromDirectory: string
  /** Set omit from directory value */
  setOmitFromDirectory: (value: string) => void
  /** Validate and submit the form */
  validateAndSubmit: () => Promise<void>
  /** Whether submit is in progress */
  isPending: boolean
}

/**
 * Hook for managing switchboard metadata editing form state
 */
export const useSwitchboardMetadata = ({
  switchboard,
  extension,
  isEditDialogOpen,
  api,
  toast,
  onSuccess,
  t,
}: UseSwitchboardMetadataProps): UseSwitchboardMetadataReturn => {
  const [name, setName] = useState<string>('')
  const [number, setNumber] = useState<string>('')
  const [omitFromDirectory, setOmitFromDirectory] = useState<string>('no')
  const queryClient = useQueryClient()

  // Pre-populate form when dialog opens
  useEffect(() => {
    if (isEditDialogOpen && extension) {
      setName(extension.name || '')
      setNumber(
        extension.number
          ? String(extension.number)
          : String(switchboard.extension_dial_plan),
      )
      const omitFromDir =
        (extension as ExtensionInstance & { omit_from_directory?: boolean })
          ?.omit_from_directory ?? false
      setOmitFromDirectory(omitFromDir ? 'yes' : 'no')
    }
  }, [isEditDialogOpen, extension, switchboard.extension_dial_plan])

  const updateSwitchboardMutation = useUpdateSwitchboard({
    api,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['switchboards'],
        })
        queryClient.invalidateQueries({
          queryKey: ['extensions'],
        })
        toast?.create({
          title: text(
            t,
            'switchboard.list.edit.toast.updated',
            'Switchboard updated',
          ),
          type: 'success',
        })
        onSuccess?.()
      },
    },
  })

  const validateAndSubmit = async () => {
    if (!name.trim()) {
      toast?.create({
        title: text(
          t,
          'switchboard.list.edit.toast.nameRequired',
          'Name Required',
        ),
        description: text(
          t,
          'switchboard.list.edit.toast.enterName',
          'Please enter a name for this switchboard.',
        ),
        type: 'error',
      })
      return
    }

    const extensionDialPlanNumberValue = parseInt(number, 10)
    if (isNaN(extensionDialPlanNumberValue)) {
      toast?.create({
        title: text(
          t,
          'switchboard.list.edit.toast.invalidNumber',
          'Invalid Number',
        ),
        description: text(
          t,
          'switchboard.list.edit.toast.enterValidNumber',
          'Please enter a valid number.',
        ),
        type: 'error',
      })
      return
    }

    if (
      extensionDialPlanNumberValue !== 0 &&
      (extensionDialPlanNumberValue < 100 ||
        extensionDialPlanNumberValue > 99999)
    ) {
      toast?.create({
        title: text(
          t,
          'switchboard.list.edit.toast.invalidNumber',
          'Invalid Number',
        ),
        description: text(
          t,
          'switchboard.list.edit.toast.numberRange',
          'Number must be 0 or between 100 and 99999.',
        ),
        type: 'error',
      })
      return
    }

    if (!switchboard.extension_dial_plan) {
      toast?.create({
        title: text(
          t,
          'switchboard.list.edit.toast.extensionDialPlanRequired',
          'Extension Dial Plan Required',
        ),
        description: text(
          t,
          'switchboard.list.edit.toast.extensionDialPlanRequiredDescription',
          'Extension dial plan is required to update the switchboard.',
        ),
        type: 'error',
      })
      return
    }

    try {
      await updateSwitchboardMutation.mutateAsync({
        extension_dial_plan: switchboard.extension_dial_plan,
        name: name.trim(),
        number: extensionDialPlanNumberValue,
        omit_from_directory: omitFromDirectory === 'yes',
      })
    } catch (error: unknown) {
      const errorTitle = text(
        t,
        'switchboard.list.edit.toast.updateFailed',
        'Failed to update switchboard',
      )
      let errorMessage = text(
        t,
        'switchboard.list.edit.toast.genericError',
        'An error occurred',
      )

      if (error && typeof error === 'object' && 'data' in error) {
        const apiError = error.data as {
          type?: string
          message?: string
          code?: string
          parameters?: Array<{
            name: string
            code: string
            message: string
          }>
        }

        if (apiError.parameters && apiError.parameters.length > 0) {
          const paramErrors = apiError.parameters
            .map((p) => `${p.name}: ${p.message}`)
            .join(', ')
          errorMessage = paramErrors
        } else if (apiError.message) {
          errorMessage = apiError.message
        }
      } else if (error instanceof Error) {
        errorMessage = error.message
      }

      toast?.create({
        title: errorTitle,
        description: errorMessage,
        type: 'error',
      })
    }
  }

  return {
    name,
    setName,
    number,
    setNumber,
    omitFromDirectory,
    setOmitFromDirectory,
    validateAndSubmit,
    isPending: updateSwitchboardMutation.isPending,
  }
}
